import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Monitors\n---\n\n## Overview\n\nStaying on top of your Snowflake environment is challenging. With thousands of active users, new workloads being added each day, and complex data pipelines, it’s difficult for platform teams to stay on top of everything going on in their account.\n\nSELECT’s Monitors feature helps Snowflake users get timely notifications about critical events in their account.\n\n## Monitor Types\n\nSELECT currently supports two types of monitors:\n\n### Digest Monitor\n\n**Digest Monitors** run on a fixed schedule and send a message to your configured destinations. Use Digest Monitors to send schedule Snowflake usage reports, receive a list of top new workloads, and more.\n\n### Threshold Monitor\n\n**Threshold Monitors** can be used to notify you if a metric exceeds a specific threshold in a given time period. Use Threshold Monitors to get notified of spend spikes, performance regressions, and more. \n\n### Anomaly Monitor\n\n**Anomaly Monitors** leverage machine learning to automatically determine whether a metric is anomalous (abnormally high or low), taking into account seasonal patterns, and notify you accordingly. Compared to Threshold Monitors, the advantage of Anomaly Monitors is you don’t need to manually set or maintain the threshold value at which you will get alerted. You can, however, optionally adjust the sensitivity of the algorithm to balance between rapid detection and minimizing false positives.\n\n### Future Monitor Types\n\nIn 2025, we will add support for the following monitor types:\n\n- **Budget Monitors** will allow you to get notified if the spend from a specific set of Snowflake resources is (a) forecasted to exceed a budget value in in a given time period or (b) has exceeded the budget value. Budget Monitors are helpful for scenarios where you want teams or groups of users to stay under a specified amount of spend in a given month, quarter or year.\n\n## Example Monitors\n\n### Daily Account Spend Digest\n\nHere's an example of a Daily Spend Digest from our Snowflake account:\n\n{% blog-post-image imageName=\"slackAlert\" alt=\"SELECT Snowflake spend alerts in slack\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20 md:w-2/3\"/%}\n\nOn this day, we had a large usage spike that was driven by Automatic Clustering being enabled for the first time. We were able to diagnose exactly what happened straight from the message, but you can also click into the relevant section of the SELECT web app (i.e. the warehouse page) to quickly understand what drove the increase.\n\n### Usage Group Spend Digest\n\nHere's an example Weekly Spend Digest configured for a [Usage Group](/docs/usage-groups):\n\n{% blog-post-image imageName=\"usageGroupDigest\" alt=\"SELECT usage group weekly spend digest\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20 md:w-2/3\" /%}\n\n### Account Spend Threshold Monitor\n\nHere's an example Slack alert for a Spend Threshold Monitor for a Snowflake account. You can also enable Threshold Monitors for other Snowflake resources like virtual warehouses.\n\n{% blog-post-image imageName=\"selectAccountSpendThreshold\" alt=\"SELECT Snowflake account spend alert\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20 md:w-2/3\" /%}\n\n### Usage Group Spend Threshold Monitor\n\nHere's an example of a Spend Threshold Monitor for all dbt models, achieved by creating a new [Usage Group](/docs/usage-groups) for dbt spend specifically.\n\n{% blog-post-image imageName=\"dbtUsageGroupThreshold\" alt=\"SELECT dbt usage group spend threshold\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20 md:w-2/3\" /%}\n\n\n";
const filepath = "/docs/monitors/overview.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
